import { Box, Skeleton } from '@mui/material';
import React, { FC } from 'react';
import { Loan } from 'v2-types/order';
import SelectField from 'components/select-field';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { getOrderProperties } from 'views/orders/mortgage-payoffs/services/queries';
import { UseFormReturn } from 'react-hook-form';
import { LoanFormType } from '../../types';
import { useTranslation } from 'react-i18next';

type Props = {
  isDataLoading: boolean;
  selectedLoan?: Loan;
  loanForm: UseFormReturn<LoanFormType, object>;
  onSave: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
};

export const Property: FC<Props> = ({ isDataLoading, selectedLoan, loanForm, onSave }) => {
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const { data: propertiesData } = useQuery(
    ['order-properties', orderId],
    () => getOrderProperties({ id: orderId }),
    {
      enabled: !!orderId,
      refetchOnWindowFocus: false,
    }
  );

  const properties = propertiesData?.getOrder.estates || [];
  const propertyValue = loanForm.watch('estate_id') || 'not-set';
  const selectedEstateInQueryResults = properties?.some(p => p._id === selectedLoan?.estate?._id);

  const dataOptions = [
    { _id: 'not-set', address: t('common:not-set') },
    ...(properties?.map((property) => ({
      _id: property._id,
      address: property.address.address,
    })) || []),
    ...(selectedLoan?.estate && !selectedEstateInQueryResults
      ? [{
        _id: selectedLoan.estate._id,
        address: selectedLoan.estate.address.address,
      }]
      : []),
  ];

  const handlePropertyChange = async (e: React.ChangeEvent<{ value: unknown }>) => {
    const value = e.target.value === 'not-set' ? '' : (e.target.value as string);
    loanForm.setValue('estate_id', value, { shouldDirty: true });
    onSave();
  };

  return (
    <Box style={{ overflow: 'hidden' }}>
      {isDataLoading && <Skeleton animation="wave" variant="text" height={40} width="70%" />}
      {selectedLoan && !isDataLoading && (
        <SelectField
          value={propertyValue}
          label={t('orders:property')}
          dataKey="_id"
          dataValue="_id"
          dataText="address"
          emptyValue={false}
          data={dataOptions}
          handleChange={handlePropertyChange}
        />
      )}
    </Box>
  );
};
